import { z } from 'zod'

const capabilitySchema = z.enum(['ENABLED', 'DISABLED'])
const e164Schema = z.string().regex(/^\+[1-9][0-9]{7,14}$/)

export const communicationsResponseSchema = z
  .object({
    schemaVersion: z.literal(2),
    status: z.enum(['COMMUNICATIONS_REQUIRED', 'REVIEW_REQUIRED']),
    revision: z.number().int().positive(),
    completionReady: z.boolean(),
    authorizedCapabilities: z
      .object({
        sms: capabilitySchema,
        voice: capabilitySchema,
        revision: z.number().int().positive(),
      })
      .strict(),
    preferences: z
      .object({
        sms: z.boolean(),
        voice: z.boolean(),
        areaCode: z
          .string()
          .regex(/^[2-9][0-9]{2}$/)
          .optional(),
        forwardingNumber: e164Schema.optional(),
        voiceRouting: z
          .object({
            frontDeskFirst: z.boolean(),
            ringCountBeforeAI: z.number().int().min(2).max(8),
          })
          .strict(),
      })
      .strict(),
    assignment: z
      .object({
        status: z.enum([
          'UNASSIGNED',
          'RESERVED',
          'ASSIGNED',
          'PROVISIONING',
          'ACTIVE',
          'RETIRED',
          'ERROR',
          'RECONCILIATION_REQUIRED',
        ]),
        publicNumber: e164Schema.optional(),
      })
      .strict(),
    smsApproval: z
      .object({
        status: z.enum([
          'UNKNOWN',
          'NOT_REQUIRED',
          'PENDING',
          'APPROVED',
          'REJECTED',
          'REMEDIATION_REQUIRED',
        ]),
      })
      .strict(),
    provisioning: z
      .object({
        status: z.enum([
          'NOT_STARTED',
          'PENDING',
          'LEASED',
          'DISPATCHING',
          'SUCCEEDED',
          'FAILED_RETRYABLE',
          'RECONCILIATION_REQUIRED',
          'FAILED_TERMINAL',
          'CANCELLED',
        ]),
      })
      .strict(),
    validation: z
      .object({
        status: z.enum([
          'NOT_RUN',
          'VALID',
          'INVALID',
          'RECONCILIATION_REQUIRED',
        ]),
        issues: z
          .array(
            z.enum([
              'ENTITLEMENT_STALE',
              'NUMBER_REQUIRED',
              'SMS_APPROVAL_REQUIRED',
              'PROVISIONING_INCOMPLETE',
              'VALIDATION_REQUIRED',
              'RUNTIME_PROJECTION_INCOMPLETE',
              'ACTIVATION_BLOCKED_PHASE_6A',
            ])
          )
          .max(20),
        lastValidatedAtMs: z.number().int().nonnegative().optional(),
      })
      .strict(),
    activation: z
      .object({
        inboundSms: z.boolean(),
        outboundSms: z.boolean(),
        voice: z.boolean(),
        followUp: z.boolean(),
        campaign: z.boolean(),
      })
      .strict(),
  })
  .strict()

export const communicationsPatchSchema = z
  .object({
    expectedRevision: z.number().int().positive(),
    sms: z.boolean(),
    voice: z.boolean(),
    areaCode: z
      .string()
      .regex(/^[2-9][0-9]{2}$/)
      .optional(),
    forwardingNumber: e164Schema.optional(),
    // Stored for older records only: inbound routing is one system-wide policy
    // (every call reaches Ava), so the Dashboard no longer sends a choice and
    // the server keeps the stored value when it is omitted.
    voiceRouting: z
      .object({
        frontDeskFirst: z.boolean(),
        ringCountBeforeAI: z.number().int().min(2).max(8),
      })
      .strict()
      .optional(),
  })
  .strict()

export type CommunicationsResponse = z.infer<
  typeof communicationsResponseSchema
>
export type CommunicationsPatch = z.infer<typeof communicationsPatchSchema>
